import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  BUTTON_BORDER_RADIUS_MAX,
  CONTACT_FORM_ALIAS_PATTERN,
  MAX_CONTACT_FORM_FIELDS,
  WIDGET_BORDER_WIDTH_MAX,
} from '../lib/constants';

export const usePresetFormSchema = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.validation' });
  const requiredString = z.string().trim().min(1, t('required'));
  const optionalUrl = z
    .string()
    .nullable()
    .refine((value) => !value || z.string().url().safeParse(value).success, t('url'));
  const optionalColor = z
    .string()
    .nullable()
    .refine((value) => !value || /^#[0-9a-f]{6}$/i.test(value), t('color'));
  const naturalNumber = z.number().int(t('integer')).min(0, t('nonNegative'));

  const schema = z.object({
    name: requiredString.max(80, t('maxLength', { max: 80 })),
    description: z
      .string()
      .trim()
      .max(500, t('maxLength', { max: 500 })),
    personal_data_policy_url: optionalUrl,
    appearance: z.object({
      brand_color: optionalColor.optional(),
      button_theme: z.enum(['light', 'dark', 'custom']),
      button_background_color: optionalColor.optional(),
      button_text_color: optionalColor.optional(),
      widget_icon: z.object({
        type: z.enum(['default', 'custom']),
        custom_url: optionalUrl.optional(),
      }),
      font_family: z.string().nullable().optional(),
    }),
    content: z.object({
      button_text: requiredString.max(80, t('maxLength', { max: 80 })),
      assistant_name: requiredString.max(80, t('maxLength', { max: 80 })),
      welcome_message: requiredString.max(1000, t('maxLength', { max: 1000 })),
      quick_questions: z.array(z.string().max(300, t('maxLength', { max: 300 }))),
    }),
    preview: z.object({
      mode: z.enum(['dynamic', 'static']),
      show_delay_seconds: naturalNumber.max(3600),
      static_question: z.string().nullable().optional(),
      static_answers: z.array(z.string().max(300, t('maxLength', { max: 300 }))),
    }),
    analytics: z.object({
      yandex_metrika: z.object({
        enabled: z.boolean(),
        counter_id: z.string().nullable().optional(),
        goals: z.array(
          z.object({
            event: z.string(),
            goal_id: z.string(),
          }),
        ),
      }),
    }),
    advanced: z.object({
      contact_form: z.object({
        mode: z.enum(['by_messages', 'by_events']),
        message_before_form: requiredString.max(1000, t('maxLength', { max: 1000 })),
        show_after_messages: naturalNumber.max(1000),
        show_close_button: z.boolean().optional(),
        show_form_after_answer: z.boolean().optional(),
        trigger_events: z.array(z.string().max(200, t('maxLength', { max: 200 }))),
        fields: z
          .array(
            z.object({
              alias: requiredString
                .max(128, t('maxLength', { max: 128 }))
                .regex(CONTACT_FORM_ALIAS_PATTERN, t('aliasFormat')),
              type: z.enum(['text', 'email', 'phone', 'foreign_phone', 'number', 'select']),
              label: requiredString.max(120, t('maxLength', { max: 120 })),
              placeholder: z
                .string()
                .nullable()
                .refine(
                  (value) => !value || value.trim().length <= 200,
                  t('maxLength', { max: 200 }),
                ),
              required: z.boolean(),
              options: z.array(requiredString.max(100, t('maxLength', { max: 100 }))).max(20),
            }),
          )
          .max(MAX_CONTACT_FORM_FIELDS),
      }),
      messages: z.object({
        border_radius: naturalNumber.max(40),
        tail_enabled: z.boolean(),
        show_agent_answer_hints: z.boolean(),
      }),
      widget: z.object({
        width_px: naturalNumber.min(320).max(800),
        height_px: naturalNumber.min(400).max(1000),
        border_width_px: naturalNumber.max(WIDGET_BORDER_WIDTH_MAX),
        border_color: z.string().regex(/^#[0-9a-f]{6}$/i, t('color')),
      }),
      button: z.object({
        border_radius: naturalNumber.max(BUTTON_BORDER_RADIUS_MAX),
        width_px: naturalNumber.min(100).max(400),
        height_px: naturalNumber.min(32).max(96),
        border_width_px: naturalNumber.max(WIDGET_BORDER_WIDTH_MAX),
        border_color: z.string().regex(/^#[0-9a-f]{6}$/i, t('color')),
      }),
      preview: z.object({
        message_border_radius: naturalNumber.max(40),
        hint_border_radius: naturalNumber.max(40),
        show_agent_answer_hints: z.boolean(),
      }),
      position: z.object({
        desktop: z.object({
          placement: z.enum(['bottom_right', 'bottom_left']),
          side_offset: naturalNumber.max(500),
          bottom_offset: naturalNumber.max(500),
          unit: z.literal('px').optional(),
        }),
        mobile: z.object({
          placement: z.enum(['bottom_right', 'bottom_left']),
          side_offset: naturalNumber.max(500),
          bottom_offset: naturalNumber.max(500),
          unit: z.literal('px').optional(),
        }),
      }),
    }),
  });

  return schema.superRefine((values, context) => {
    if (values.appearance.button_theme === 'custom') {
      if (!values.appearance.button_background_color) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('required'),
          path: ['appearance', 'button_background_color'],
        });
      }
      if (!values.appearance.button_text_color) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('required'),
          path: ['appearance', 'button_text_color'],
        });
      }
    }

    if (
      values.appearance.widget_icon.type === 'custom' &&
      !values.appearance.widget_icon.custom_url
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('required'),
        path: ['appearance', 'widget_icon', 'custom_url'],
      });
    }

    if (
      values.analytics.yandex_metrika.enabled &&
      !values.analytics.yandex_metrika.counter_id?.trim()
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('required'),
        path: ['analytics', 'yandex_metrika', 'counter_id'],
      });
    }

    if (values.analytics.yandex_metrika.enabled) {
      values.analytics.yandex_metrika.goals.forEach((goal, index) => {
        if (!goal.event.trim()) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required'),
            path: ['analytics', 'yandex_metrika', 'goals', index, 'event'],
          });
        }
        if (!goal.goal_id.trim()) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('required'),
            path: ['analytics', 'yandex_metrika', 'goals', index, 'goal_id'],
          });
        }
      });
    }

    if (
      values.advanced.contact_form.mode === 'by_events' &&
      !values.advanced.contact_form.trigger_events.some((event) => event.trim())
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('required'),
        path: ['advanced', 'contact_form', 'trigger_events'],
      });
    }

    const aliases = values.advanced.contact_form.fields.map((field) =>
      field.alias.trim().toLowerCase(),
    );
    values.advanced.contact_form.fields.forEach((field, index) => {
      if (aliases.indexOf(aliases[index]) !== index) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('aliasUnique'),
          path: ['advanced', 'contact_form', 'fields', index, 'alias'],
        });
      }

      if (field.type !== 'select') return;

      const normalizedOptions = field.options.map((option) => option.trim().toLowerCase());
      if (normalizedOptions.length < 2) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('choiceOptionsMinimum'),
          path: ['advanced', 'contact_form', 'fields', index, 'options'],
        });
      }

      if (new Set(normalizedOptions).size !== normalizedOptions.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('choiceOptionsUnique'),
          path: ['advanced', 'contact_form', 'fields', index, 'options'],
        });
      }
    });
  });
};
